/**
 * O token, em memória e só em memória (H01).
 *
 * ── A regra, e por que ela é dura ─────────────────────────────────────────
 *
 * A H01 é explícita: *"Token nunca em `localStorage`. `localStorage` é legível
 * por qualquer script — inclusive por dependência comprometida."*
 *
 * `sessionStorage` tem exatamente o mesmo problema: muda o tempo de vida, não
 * quem consegue ler. Uma biblioteca de gráfico comprometida lê os dois com uma
 * linha. Este módulo é um objeto de módulo, e um objeto de módulo só é
 * alcançável por quem tem a referência.
 *
 * Isso não impede tudo — script injetado na mesma página pode chamar `fetch` em
 * nome do usuário de qualquer forma. O que impede é a EXFILTRAÇÃO: sem o token
 * em mãos, o atacante age enquanto a aba está aberta e para quando ela fecha,
 * em vez de sair com uma credencial de 15 minutos e um refresh de 30 dias.
 *
 * ── O preço, e o que se faz com ele ───────────────────────────────────────
 *
 * Recarregar a página apaga o cofre. Quem resolve isso é a sessão do provedor:
 * o `renovarEmSilencio` do `fluxo-oidc.ts` recupera o token sem pedir senha,
 * porque o cookie de SSO do Keycloak continua lá. Guardar token no navegador
 * para economizar uma ida ao provedor seria trocar a única defesa real por
 * duzentos milissegundos.
 */

/** O que o provedor devolve, já normalizado. */
export interface Credencial {
  readonly acesso: string;
  /** Instante de expiração em epoch ms — absoluto, não duração. */
  readonly expiraEm: number;
  /**
   * O refresh também mora aqui, e também só na memória.
   *
   * O realm liga `revokeRefreshToken` com `refreshTokenMaxReuse: 0`: o antigo
   * morre no instante em que o novo é emitido. Se ele reaparecer, o Keycloak
   * entende que vazou e derruba a sessão inteira. Persistir o refresh
   * transformaria uma aba duplicada em incidente de segurança.
   */
  readonly refresh: string | null;
  /** Necessário para o logout no provedor (`id_token_hint`). */
  readonly idToken: string | null;
  /** Métodos de autenticação usados, do claim `amr` (B15). */
  readonly amr: readonly string[];
}

let credencial: Credencial | null = null;

/** Quem quer saber quando o cofre muda — o React, hoje. */
type Ouvinte = (atual: Credencial | null) => void;
const ouvintes = new Set<Ouvinte>();

function avisar(): void {
  for (const ouvinte of ouvintes) ouvinte(credencial);
}

export function observarCofre(ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

export function guardar(nova: Credencial): void {
  credencial = nova;
  avisar();
}

export function ler(): Credencial | null {
  return credencial;
}

export function limpar(): void {
  credencial = null;
  avisar();
}

/**
 * Margem de renovação: renova antes de vencer, não depois.
 *
 * Sessenta segundos cobrem o relógio do navegador estar adiantado, a rede estar
 * lenta e a requisição já ter saído quando o token virou. Renovar no instante
 * exato produziria o 401 esporádico que ninguém consegue reproduzir.
 */
export const MARGEM_DE_RENOVACAO_MS = 60_000;

export function precisaRenovar(agora = Date.now(), atual = credencial): boolean {
  if (!atual) return false;
  return atual.expiraEm - agora <= MARGEM_DE_RENOVACAO_MS;
}

/** Já venceu de fato — nem margem salva. */
export function venceu(agora = Date.now(), atual = credencial): boolean {
  if (!atual) return true;
  return atual.expiraEm <= agora;
}

/**
 * Quanto falta para a próxima renovação, em ms.
 *
 * Nunca negativo: um atraso negativo em `setTimeout` dispara imediatamente e,
 * se a renovação falhar em silêncio, vira laço apertado contra o provedor.
 */
export function atrasoAteRenovar(agora = Date.now(), atual = credencial): number {
  if (!atual) return Number.POSITIVE_INFINITY;
  return Math.max(0, atual.expiraEm - agora - MARGEM_DE_RENOVACAO_MS);
}
